import { FC } from "react"

import { useTranslation } from "react-i18next"

import { CurrencyFiatLimit } from "services/settings/type"

import { LimitsCurrencyCard } from "./LimitsCurrencyCard"

interface LimitsPerCurrencySectionProps {
  currencyLimits: Record<string, CurrencyFiatLimit>
  // Platform currencies still available to add (USD and already-added ones excluded).
  availableCurrencies: string[]
  onAddCurrency: (code: string) => void
  onRemoveCurrency: (code: string) => void
  onUpdate: (path: string[], value: string) => void
  disabled?: boolean
}

// Per-currency fiat limits: an "add currency" picker plus a card per configured currency.
export const LimitsPerCurrencySection: FC<LimitsPerCurrencySectionProps> = ({
  currencyLimits,
  availableCurrencies,
  onAddCurrency,
  onRemoveCurrency,
  onUpdate,
  disabled
}) => {
  const { t } = useTranslation("translation", { useSuspense: false })

  const entries = Object.entries(currencyLimits)

  return (
    <div className="mt-8 border-t border-black/10 dark:border-white/10 pt-8">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h4 className="text-lg font-semibold text-[rgba(6,7,21,1)] dark:text-white">{t("settings.limits.perCurrency.title")}</h4>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-2xl">{t("settings.limits.perCurrency.description")}</p>
        </div>
        <select
          id="limits-add-currency"
          className="px-3 py-2 text-sm rounded-lg border bg-white text-black border-black/10 dark:bg-[#0C0C1A] dark:text-white dark:border-white/10"
          value=""
          onChange={(e) => {
            onAddCurrency(e.target.value)
            e.target.value = ""
          }}
          disabled={disabled || availableCurrencies.length === 0}
        >
          <option value="" disabled>
            {availableCurrencies.length ? t("settings.limits.perCurrency.addCurrency") : t("settings.limits.perCurrency.allAdded")}
          </option>
          {availableCurrencies.map((code) => (
            <option key={code} value={code}>
              {code}
            </option>
          ))}
        </select>
      </div>

      {entries.length === 0 ? (
        <p className="text-sm text-gray-400 mt-6">{t("settings.limits.perCurrency.none")}</p>
      ) : (
        <div className="flex flex-col gap-6 mt-6">
          {entries.map(([code, cfg]) => (
            <LimitsCurrencyCard
              key={code}
              code={code}
              value={cfg}
              onUpdate={onUpdate}
              onRemove={onRemoveCurrency}
              disabled={disabled}
            />
          ))}
        </div>
      )}
    </div>
  )
}
